<template>
    <rkm-modal
        :title="`Solicitações (${paciente.nome})`"
        extraLarge
        ref="Modal"
    >
        <rkm-table
            :fields="fields"
            :items="Array.isArray(solicitacoes) ? solicitacoes : []"
            :tbody-tr-class="rowClass"
            class="table-solicitacao"
            sort-asc
            sort-by="data"
        >
            <template v-slot:cell(data)="{ value, item }">
                {{ value }}

                <rkm-icon
                    v-if="item.hasAltaOci"
                    icon="check-circle"
                    title="Protocolo / OCI encerrada"
                />

                <rkm-icon
                    v-else-if="item.hasOci"
                    icon="clipboard-list"
                    title="Protocolo / OCI ativa"
                />

                <rkm-icon
                    v-if="item.isOciVencida"
                    icon="exclamation-triangle"
                    title="Protocolo / OCI vencida"
                />
            </template>

            <template v-slot:cell(itens)="{ item }">
                <div v-for="(itemSub, key) in item.itens" :key="key">
                    {{ itemSub }}
                </div>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <div>
                    <rkm-button
                        @click="$refs.modalDetalhes.open(item)"
                        color="primary"
                        small
                        title="Detalhes da solicitação"
                    >
                        <rkm-icon icon="eye" />
                    </rkm-button>

                    <rkm-button
                        v-if="item.allowUpdate"
                        :disabled="!item.allowUpdate || !item.confirmado"
                        @click="handleEdit(item.id)"
                        color="primary"
                        small
                        title="Editar"
                    >
                        <rkm-icon icon="pen" />
                    </rkm-button>

                    <rkm-button
                        v-if="(item.allowConfirma || !item.confirmado) || item.allowConfirma"
                        :disabled="item.confirmado"
                        @click="handleEdit(item.id)"
                        color="success"
                        small
                        title="Confirmar"
                    >
                        <rkm-icon icon="check-circle" />
                    </rkm-button>

                    <rkm-button
                        :disabled="!item.allowCancel"
                        @click="$refs.modalJustificativa.open(item.id)"
                        color="danger"
                        small
                        title="Cancelar"
                    >
                        <rkm-icon icon="times-circle" />
                    </rkm-button>

                    <rkm-button
                        @click="handleModalPrint(item)"
                        color="secondary"
                        small
                        title="Imprimir protocolo"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>
                </div>

                <modalDetalhes
                    ref="modalDetalhes"
                   :item="item"
                />
            </template>
        </rkm-table>

        <rkm-row class="mt-2">
            <rkm-col cols="12">
                <p class="m-0">
                    A linha em <span class="yellow">amarelo</span> representa que a solicitação não foi confirmada.<br>
                    A linha em <span class="blue">azul</span> representa que a prioridade da solicitação é eletivo/normal.<br>
                    A linha em <span class="orange">laranja</span> representa que a prioridade da solicitação é urgência.<br>
                    A linha em <span class="green">verde</span> representa que a prioridade da solicitação é prioritário.<br>
                    A linha em <span class="danger">vermelho</span> representa que a prioridade da solicitação é emergência.<br>
                </p>
            </rkm-col>
        </rkm-row>

        <template v-slot:footer="{ cancel }">
            <rkm-button
                :disabled="!!loading"
                @click="cancel"
            >
                Fechar
            </rkm-button>

            <rkm-button
                @click="createSolicitacao(paciente.classificacaoId, solicitacoes[0] || solicitacoes)"
                :isVisualizacao="false"
                authorize="create-regulacao-solicitacao"
                color="primary"
            >
                Cadastrar solicitação
            </rkm-button>
        </template>

                <modalJustificativa
                    @refresh="load"
                    ref="modalJustificativa"
                />
                <modalImpressao
                    ref="ModalImpressao"
                    :origem="'solicitacao'"
                />

    </rkm-modal>
</template>
<script>

// Components.
import modalJustificativa from './components/justficativa/Modal'
import modalDetalhes from './components/detalhes/Modal'
import modalImpressao from '@/components/regulacao/fila/components/impressao/Modal'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

// Mixins.
import { repository, stringCase, object, error, formats, file } from '@/mixins'

// Enums.
import prioridadeEnum from '@/enums/regulacao/prioridade'
import classificacaopacienteEnum from '@/enums/cadastro/paciente/classificacao'

export default {
    components: {
        modalJustificativa,
        modalDetalhes,
        modalImpressao,
    },
    created() {
        this.repositories = {
            solicitacao: this.getConectaSusRepository('regulacao.solicitacao'),
            fila: this.getConectaSusRepository('regulacao.fila'),
        }
    },
    computed: {
        ...mapGetters({ loading: 'getLoading' }),
        fields() {
            return [
                {
                    key: 'data',
                    label: 'Data',
                    class: 'col-min text-center',
                    formatter: value => this.formatDate(value),
                    filterByFormatted: true,
                    sortable: true
                },
                {
                    key: 'statusNome',
                    label: 'Status',
                    sortable: true
                },
                {
                    key: 'usuarioSolicitanteNome',
                    label: 'Usuário solicitante',
                    sortable: true
                },
                {
                    key: 'infraestruturaSolicitanteNome',
                    label: 'Unidade solicitante',
                    sortable: true
                },
                {
                    key: 'unidadeReguladoraNomeFantasia',
                    label: 'Unidade reguladora',
                    sortable: true
                },
                {
                    key: 'itens',
                    label: 'Procedimento(s) solicitado(s)',
                },
                {
                    key: 'prioridadeNome',
                    label: 'Prioridade',
                    sortable: true
                },
                {
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min text-center'
                },
            ]
        }
    },
    data: () => ({
        paciente: {},
        solicitacoes: [],
        enums: {
            prioridadeEnum,
            classificacaopacienteEnum,
        }
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(paciente) {

            this.paciente = paciente
            this.solicitacoes = []
            this.$refs.Modal.show()
            this.load()
        },
        load() {
            this.setLoading()
            this.repositories.solicitacao.index(this.paciente.id)
                .then(response => {
                    this.solicitacoes = response.data
                })
                .catch(this.showError)
                .finally(() => this.unsetLoading())
        },
        rowClass(item, type) {
            if (type === 'row' && item) {

                if (!item.confirmado) {

                    return 'bg-yellow'
                }

                switch (item.prioridadeId) {

                    case this.enums.prioridadeEnum.ELETIVO: {

                        return 'table-primary'
                    }
                    case this.enums.prioridadeEnum.URGENCIA: {

                        return 'table-orange'
                    }
                    case this.enums.prioridadeEnum.PRIORITARIO: {

                        return 'table-success'
                    }
                    case this.enums.prioridadeEnum.EMERGENCIA: {

                        return 'table-danger'
                    }
                    default: {

                        return ''
                    }
                }
            }
        },
        handleEdit(id) {
            this.$router.push({ name: 'regulacao-solicitacao-edit', params: { id: id, pacienteId: this.paciente.id } })
        },
        createSolicitacao(classificacao, solicitacao) {
            if (classificacao === this.enums.classificacaopacienteEnum.OUTRO_MUNICIPIO && solicitacao.isPpi == false) {

                this.showWarningNotification(
                    `A unidade logada não pode cadastrar solicitações para pacientes de outro município.`,
                );
            } else if ((classificacao === this.enums.classificacaopacienteEnum.OUTRO_MUNICIPIO) && solicitacao.unidadeAtendeOutroMunicipio == false) {

                this.showWarningNotification(
                    `A unidade logada não pode cadastrar solicitações para pacientes de outro município.`,
                );
            } else {

                this.$router.push({ name: 'regulacao-solicitacao-create', params: { pacienteId: this.paciente.id } })
            }
        },
        handleModalPrint(solicitacao) {

            this.setLoading()

            this.repositories.fila.getAtendimentos({id: solicitacao.id})
                .then(({ data }) => {

                    solicitacao['agendamentoData'] = data

                    if (data && data.sistema) {
                        solicitacao['sistema'] = data.sistema
                    }

                    if (data && data.examesIncluidos) {
                        solicitacao['examesIncluidos'] = data.examesIncluidos
                    }

                    if (data && data.infraestruturaSolicitante) {
                        solicitacao['infraestruturaSolicitante'] = data.infraestruturaSolicitante
                    }

                    if (data && data.prestadorExterno) {
                        solicitacao['prestadorExterno'] = data.prestadorExterno
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())

            this.$refs.ModalImpressao.open(solicitacao);
        },
    },
    mixins: [repository, stringCase, object, error, formats, file ]
}
</script>

<style scoped>
.table-solicitacao >>> .bg-yellow {
    background-color: #fffae0;
}
.table-solicitacao >>> .table-orange {
    background-color: #ffa070;
}
.yellow {
    color: var(--warning);
}
.blue {
    color: var(--primary);
}
.orange {
    color: #e9763c;
}
.green {
    color: var(--success);
}
.danger {
    color: var(--danger);
}
</style>